import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { PhoneConfigItem, PhoneConfigUpsert } from '@/types/api';

interface Props {
  initial?: PhoneConfigItem;
  onSubmit: (phone: string, body: PhoneConfigUpsert) => void;
  onCancel: () => void;
  pending: boolean;
}

export default function PhoneConfigForm({ initial, onSubmit, onCancel, pending }: Props) {
  const { t } = useTranslation(['phone', 'errors']);
  const [phone, setPhone] = useState(initial?.phone_number ?? '');
  const [tasks, setTasks] = useState(initial?.tasks ?? 0);
  const [note, setNote] = useState(initial?.note ?? '');
  const [instructions, setInstructions] = useState(initial?.instructions ?? '');
  const [language, setLanguage] = useState(initial?.language ?? '');
  const [langCands, setLangCands] = useState((initial?.lang_candidates ?? []).join(', '));
  const [err, setErr] = useState<string | null>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!initial && !phone.trim()) {
      setErr(t('phone:errors.invalidPhone'));
      return;
    }
    if (tasks < 0) {
      setErr(t('phone:errors.invalidTasks'));
      return;
    }
    setErr(null);
    const candidates = langCands
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    onSubmit(phone, {
      tasks,
      note: note || null,
      instructions: instructions || null,
      language: language || null,
      lang_candidates: candidates.length ? candidates : null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 text-xs">
      <div>
        <Label htmlFor="pc-phone">{t('phone:form.phone')}</Label>
        <Input
          id="pc-phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={!!initial}
          required
        />
        <p className="mt-1 text-muted-foreground">{t('phone:form.phoneHint')}</p>
      </div>
      <div>
        <Label htmlFor="pc-tasks">{t('phone:form.tasks')}</Label>
        <Input
          id="pc-tasks"
          type="number"
          min={0}
          value={tasks}
          onChange={(e) => setTasks(Number(e.target.value))}
          required
        />
        <p className="mt-1 text-muted-foreground">{t('phone:form.tasksHint')}</p>
      </div>
      <div>
        <Label htmlFor="pc-note">{t('phone:form.note')}</Label>
        <Input id="pc-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div>
        <Label htmlFor="pc-instr">{t('phone:form.instructions')}</Label>
        <Input
          id="pc-instr"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
        <p className="mt-1 text-muted-foreground">{t('phone:form.instructionsHint')}</p>
      </div>
      <div>
        <Label htmlFor="pc-lang">{t('phone:form.language')}</Label>
        <Input
          id="pc-lang"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          placeholder="uk"
        />
        <p className="mt-1 text-muted-foreground">{t('phone:form.languageHint')}</p>
      </div>
      <div>
        <Label htmlFor="pc-cands">{t('phone:form.langCandidates')}</Label>
        <Input
          id="pc-cands"
          value={langCands}
          onChange={(e) => setLangCands(e.target.value)}
          placeholder="uk, ru, en"
        />
        <p className="mt-1 text-muted-foreground">{t('phone:form.langCandidatesHint')}</p>
      </div>
      {err && (
        <div role="alert" className="text-destructive">
          {err}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('phone:form.cancel')}
        </Button>
        <Button type="submit" size="sm" disabled={pending}>
          {t('phone:form.save')}
        </Button>
      </div>
    </form>
  );
}
